/**
 * Fractional indexing: string keys that sort lexicographically and allow
 * inserting a key between any two keys. Keys are base-62 fractions in (0, 1)
 * without trailing '0'.
 */
const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = DIGITS.length;

function digit(c: string | undefined): number {
  if (c === undefined) return 0;
  const d = DIGITS.indexOf(c);
  if (d < 0) throw new Error(`invalid fractional index digit: ${c}`);
  return d;
}

function midpoint(a: string, b: string | null): string {
  if (b !== null && a >= b) throw new Error(`${a} >= ${b}`);
  if (b !== null) {
    let n = 0;
    while ((a[n] ?? '0') === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const da = digit(a[0]);
  const db = b !== null ? digit(b[0]) : BASE;
  if (db - da > 1) return DIGITS[Math.round((da + db) / 2)]!;
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return DIGITS[da]! + midpoint(a.slice(1), null);
}

/** Key strictly between a and b (null = open end). */
export function keyBetween(a: string | null, b: string | null): string {
  if (a !== null && b !== null && a >= b) throw new Error(`keyBetween: ${a} >= ${b}`);
  return midpoint(a ?? '', b);
}

/** n ascending keys strictly between a and b. */
export function keysBetween(a: string | null, b: string | null, n: number): string[] {
  if (n <= 0) return [];
  if (n === 1) return [keyBetween(a, b)];
  const mid = keyBetween(a, b);
  const left = Math.floor((n - 1) / 2);
  return [...keysBetween(a, mid, left), mid, ...keysBetween(mid, b, n - 1 - left)];
}

/** Comparator for objects ordered by z, ties broken by id. */
export function compareZ(a: { z: string; id: string }, b: { z: string; id: string }): number {
  if (a.z < b.z) return -1;
  if (a.z > b.z) return 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
